import React, { useMemo } from "react";
import "../Logs.css";
import moment from "moment-timezone";
import { useLogs } from "../hooks/useLogs";
import StatusBadge from "../../../shared/components/common/StatusBadge";
import Table from "../../../shared/components/common/Table";
import FilterModal from "./FilterModal";
import LogsToolbar from "./LogsToolbar";
import Tooltip from "../../../shared/components/common/Tooltip";
import InfoIcon from "@mui/icons-material/InfoOutlined";
import { PageHeader } from "../../../shared/components/common";

const extractCallerId = (subject) => {
  const match = subject?.match(/from (.+)/i);
  return match ? match[1] : "-";
};

const normalizeStatusToken = (value) =>
  String(value || "")
    .trim()
    .toUpperCase()
    .replace(/[^A-Z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "");

const getNormalizedStatus = (log) => {
  const jobStatus = normalizeStatusToken(log?.job_status);
  const deliveryStatus = normalizeStatusToken(log?.delivery_status);
  const deliveryStatusMap = {
    DELIVERED: "delivered",
    FAILED: "failed",
    PROCESSING: "processing",
  };

  if (deliveryStatusMap[deliveryStatus]) {
    return deliveryStatusMap[deliveryStatus];
  }

  if (
    ["PROCESS_COMPLETED", "DELIVERED", "SUCCESS"].includes(jobStatus) ||
    ["DELIVERED", "SUCCESS"].includes(deliveryStatus)
  ) {
    return "delivered";
  }

  if (
    jobStatus.includes("FAIL") ||
    jobStatus.includes("ERROR") ||
    jobStatus.includes("INVALID") ||
    jobStatus.includes("NO_COMPANY") ||
    jobStatus.includes("NOT_DELIVERED") ||
    deliveryStatus.includes("FAIL") ||
    deliveryStatus.includes("ERROR") ||
    deliveryStatus.includes("INVALID") ||
    deliveryStatus.includes("NO_COMPANY") ||
    deliveryStatus.includes("NOT_DELIVERED")
  ) {
    return "failed";
  }

  if (
    ["UPLOADED", "PROCESSING", "IN_PROGRESS", "PENDING", "QUEUED", "STARTED"].includes(jobStatus) ||
    ["PROCESSING", "IN_PROGRESS", "PENDING", "QUEUED", "STARTED"].includes(deliveryStatus)
  ) {
    return "processing";
  }

  return "";
};

const toStatusLabel = (statusValue) => {
  if (statusValue === "delivered") return "Delivered";
  if (statusValue === "failed") return "Failed";
  if (statusValue === "processing") return "Processing";
  return "-";
};

const Logs = () => {
  const {
    filteredLogs,
    isLoading,
    currentPage,
    setCurrentPage,
    searchQuery,
    setSearchQuery,
    deliveryStatusFilter,
    setDeliveryStatusFilter,
    dateRange,
    setDateRange,
    spreadTable,
    setSpreadTable,
    showFilterModal,
    setShowFilterModal,
    handleClearSearch,
  } = useLogs();

  // Date range options
  const dateRangeOptions = [
    { name: "All Time", value: "all" },
    { name: "Today", value: "today" },
    { name: "This Week", value: "thisweek" },
    { name: "Last Week", value: "lastweek" },
  ];

  // Status filter options - map to API values
  const statusOptions = [
    { name: "All Status", value: "" },
    { name: "Failed", value: "failed" },
    { name: "Processing", value: "processing" },
    { name: "Delivered", value: "delivered" },
  ];

  // Apply client-side filters
  const displayLogs = useMemo(() => {
    let filtered = Array.isArray(filteredLogs) ? filteredLogs : [];

    if (dateRange !== "all") {
      const now = moment();
      filtered = filtered.filter((log) => {
        const logDate = moment(log.created_time);
        switch (dateRange) {
          case "today":
            return logDate.isSame(now, "day");
          case "thisweek":
            return logDate.isSame(now, "week");
          case "lastweek":
            return logDate.isSame(now.clone().subtract(1, "week"), "week");
          default:
            return true;
        }
      });
    }

    if (searchQuery && searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter((log) => {
        const userName = (log.email_from_name || "").toLowerCase();
        const callerId = (log.email_from_address || "").toLowerCase();
        const deliverTo = (log.to_email_addresses || "").toLowerCase();
        return (
          userName.includes(query) ||
          callerId.includes(query) ||
          deliverTo.includes(query)
        );
      });
    }

    if (deliveryStatusFilter) {
      const selectedStatus = String(deliveryStatusFilter).toLowerCase();
      filtered = filtered.filter((log) => {
        return getNormalizedStatus(log) === selectedStatus;
      });
    }

    return filtered.map((log) => ({
      id: log.id || log.job_name,
      createdDateTime: moment
        .tz(new Date(log.created_time), "America/Los_Angeles")
        .format("YYYY-MM-DD HH:mm:ss"),
      callerId: extractCallerId(log.email_subject || ""),
      userName: log.email_from_name || "-",
      deliverTo: log.to_email_addresses || "-",
      status: toStatusLabel(getNormalizedStatus(log)),
      originalLog: log,
    }));
  }, [filteredLogs, dateRange, searchQuery, deliveryStatusFilter]);

  const hasAnyLogs = (filteredLogs || []).length > 0;
  const hasActiveFilters =
    Boolean(searchQuery && searchQuery.trim()) ||
    Boolean(deliveryStatusFilter) ||
    dateRange !== "all";

  const columns = useMemo(
    () => [
      {
        Header: () => (
          <div className="flex items-center gap-1">
            <span>DATE/TIME</span>
            <Tooltip text="Date & time PBXScribe received voicemail message">
              <InfoIcon sx={{ fontSize: "1rem", color: "gray" }} />
            </Tooltip>
          </div>
        ),
        accessor: "createdDateTime",
        Cell: ({ value }) => {
          const date = moment(value, "YYYY-MM-DD HH:mm:ss");
          return (
            <span className="text-content text-xs md:text-sm">
              {date.format("MM/DD/YYYY hh:mm A")}
            </span>
          );
        },
      },
      {
        Header: "CALLER ID",
        accessor: "callerId",
        Cell: ({ value }) => (
          <span className="text-content text-xs md:text-sm">{value}</span>
        ),
      },
      {
        Header: "USER NAME",
        accessor: "userName",
        Cell: ({ value }) => (
          <span className="text-content text-xs md:text-sm">{value}</span>
        ),
      },
      {
        Header: () => (
          <div className="flex items-center gap-1">
            <span>DELIVER TO</span>
            <Tooltip text="End user email address to deliver transcribed message to">
              <InfoIcon sx={{ fontSize: "1rem", color: "gray" }} />
            </Tooltip>
          </div>
        ),
        accessor: "deliverTo",
        Cell: ({ value }) => (
          <span className="text-content text-xs md:text-sm">{value}</span>
        ),
      },
      {
        Header: "STATUS",
        accessor: "status",
        disableSortBy: true,
        Cell: ({ value }) => <StatusBadge status={value} />,
      },
    ],
    [],
  );

  return (
    <div className="flex flex-col w-full h-full bg-transparent">
      <PageHeader
        title="Message Logs & Tracking"
        subtitle="Monitor voicemail processing and delivery status"
      />

      <div
        className={`lg:mx-4 border border-line rounded-t-xl overflow-auto shadow-sm flex flex-col flex-1 logs-table-area${
          spreadTable ? " table-zoom-out" : ""
        }`}
      >
        <LogsToolbar
          searchQuery={searchQuery}
          setSearchQuery={setSearchQuery}
          spreadTable={spreadTable}
          setSpreadTable={setSpreadTable}
          setShowFilterModal={setShowFilterModal}
          statusFilter={deliveryStatusFilter}
          setStatusFilter={setDeliveryStatusFilter}
          statusOptions={statusOptions}
          displayLogs={displayLogs}
          columns={columns}
        />
        {!isLoading && displayLogs.length === 0 ? (
          <div className="w-full h-full px-6 py-10 bg-white rounded-b-xl text-center flex flex-col items-center justify-center">
            <p className="text-base lg:text-lg font-semibold text-primary mb-1">
              No logs available
            </p>
            <p className="text-xs lg:text-base text-gray-500">
              {hasAnyLogs
                ? "No logs match the current filters."
                : "There are no logs to display. Please check back later when messages have been processed."}
            </p>
            {hasAnyLogs && hasActiveFilters ? (
              <button
                type="button"
                onClick={handleClearSearch}
                className="mt-3 text-sm font-medium text-primary px-2 py-1 rounded-lg border border-primary/50 hover:bg-primary/10 transition"
              >
                Clear filters
              </button>
            ) : null}
          </div>
        ) : (

          <>
            {/* Filter Modal */}
            <FilterModal
              isOpen={showFilterModal}
              onClose={() => setShowFilterModal(false)}
              searchQuery={searchQuery}
              setSearchQuery={setSearchQuery}
              statusFilter={deliveryStatusFilter}
              setStatusFilter={setDeliveryStatusFilter}
              dateRange={dateRange}
              setDateRange={setDateRange}
              statusOptions={statusOptions}
              dateRangeOptions={dateRangeOptions}
            />

            {/* Table */}
            <div className="flex-1 overflow-auto logs-table-area">
              <Table
                columns={columns}
                data={displayLogs}
                currentPage={currentPage}
                setCurrentPage={setCurrentPage}
                showTotalRecords={true}
                totalRecords={displayLogs.length}
                showPageSizeSelector={true}
                useMUIcons={false}
                loading={isLoading}
              />
            </div>
          </>
        )}
      </div>

      {/* Message Details Modal */}
      {/* <MessageDetailsModal
        isOpen={showModal}
        onClose={() => setShowModal(false)}
        log={selectedLog}
      /> */}
    </div>
  );
};

export default Logs;
